import { Download } from "@mui/icons-material";
import { FC, useState, useRef, useEffect } from "react";
import avatar from "src/assets/images/header/avatar.svg";
import { formatDateDmy, formatTime } from "src/utils";

// Import CallLogs type
interface CallLogs {
	_id: string;
	sender: string;
	booking_id: string;
	message: string;
	message_type: string;
	message_read: boolean;
	call_status: boolean;
	chat_view_status: boolean;
	message_date: string;
	createdAt: string;
	updatedAt: string;
	__v: number;
	call_duration: number;
}

const CallDetailsModal: FC<{
	open: boolean;
	handleClose: () => void;
	call?: any;
}> = ({ open, handleClose, call }) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const audioRef = useRef<HTMLAudioElement | null>(null);

	// Format call duration from seconds to minutes:seconds
	const formatDuration = (seconds: number) => {
		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
	};

	const startTime = formatTime(new Date(call?.originalData?.createdAt));
	const endTime = formatTime(new Date(call?.originalData?.updatedAt));
	const endTimeFormatted = endTime;
	const callRecordingUrl =
		call?.originalData?.call_meta?.agora_call_recording_url;

	// Audio playback functions
	const togglePlay = () => {
		if (!audioRef.current || !callRecordingUrl) return;

		if (isPlaying) {
			audioRef.current.pause();
		} else {
			audioRef.current.play();
		}
		setIsPlaying(!isPlaying);
	};

	const handleTimeUpdate = () => {
		if (audioRef.current) {
			setCurrentTime(audioRef.current.currentTime);
		}
	};

	const handleLoadedMetadata = () => {
		if (audioRef.current) {
			setDuration(audioRef.current.duration);
		}
	};

	const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
		const time = parseFloat(e.target.value);
		if (audioRef.current) {
			audioRef.current.currentTime = time;
			setCurrentTime(time);
		}
	};

	const handleDownload = async () => {
		if (!callRecordingUrl) {
			console.error("No call recording URL available");
			return;
		}

		setIsLoading(true);
		try {
			// Method 1: Try direct download first
			const response = await fetch(callRecordingUrl, {
				method: "GET",
				headers: {
					Accept: "audio/*",
				},
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const blob = await response.blob();

			// Check if blob is valid
			if (blob.size === 0) {
				throw new Error("Downloaded file is empty");
			}

			// Create download link
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `call-recording-${
				call?.originalData?._id || Date.now()
			}.mp3`;
			a.style.display = "none";

			document.body.appendChild(a);
			a.click();

			// Cleanup
			setTimeout(() => {
				window.URL.revokeObjectURL(url);
				document.body.removeChild(a);
			}, 100);
		} catch (error) {
			console.error("Download failed:", error);

			// Method 2: Try opening in new tab if direct download fails
			try {
				console.log("Attempting to open recording in new tab...");
				window.open(callRecordingUrl, "_blank");
			} catch (fallbackError) {
				console.error("Fallback download also failed:", fallbackError);
				// You could add a toast notification here
				alert(
					'Download failed. Please try right-clicking the play button and selecting "Save audio as..."'
				);
			}
		} finally {
			setIsLoading(false);
		}
	};

	// Cleanup audio when modal closes
	useEffect(() => {
		if (!open && audioRef.current) {
			audioRef.current.pause();
			setIsPlaying(false);
			setCurrentTime(0);
		}
	}, [open]);

	// Reset audio when call changes
	useEffect(() => {
		if (audioRef.current) {
			audioRef.current.pause();
			setIsPlaying(false);
			setCurrentTime(0);
			setDuration(0);
		}
	}, [call]);

	if (!open || !call) return null;

	return (
		<>
			<div
				className='fixed inset-0 z-40 bg-black/30 transition-opacity'
				onClick={handleClose}
			/>
			<div className='fixed top-0 right-0 z-50 h-full w-full max-w-[480px] bg-white shadow-2xl flex flex-col'>
				{/* Header */}
				<div className='flex items-center gap-3 px-6 py-4 border-b border-gray-100 relative'>
					<button
						className='mr-2 p-1 rounded-full hover:bg-gray-100'
						onClick={handleClose}
					>
						<svg
							width='24'
							height='24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							viewBox='0 0 24 24'
						>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								d='M15 19l-7-7 7-7'
							/>
						</svg>
					</button>
					<div className='flex-1 text-center font-semibold text-lg text-gray-800'>
						Call Details
					</div>
					<button className='ml-auto p-1 rounded-full hover:bg-gray-100'>
						<svg
							width='24'
							height='24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							viewBox='0 0 24 24'
						>
							<circle cx='12' cy='6' r='1.5' />
							<circle cx='12' cy='12' r='1.5' />
							<circle cx='12' cy='18' r='1.5' />
						</svg>
					</button>
				</div>
				{/* Avatars and call icon */}
				<div className='flex flex-col items-center mt-8 mb-8 px-6'>
					<div className='flex items-center justify-center w-full relative'>
						<div className='flex flex-col items-center'>
							<img
								src={call.avatars[0] || avatar}
								alt='caller'
								className='w-24 h-24 rounded-full object-cover border-4 border-white shadow'
							/>
							<span className='mt-2 text-gray-700 font-medium'>
								{call.from}
							</span>
						</div>
						<div className='absolute left-1/2 top-0 -translate-x-1/2 flex flex-col items-center'>
							<svg width='80' height='40' className='mb-1' viewBox='0 0 80 40'>
								<path
									d='M0,40 Q40,0 80,40'
									stroke='#aaa'
									strokeDasharray='6,6'
									fill='none'
								/>
							</svg>
							<img src='/svgs/phone2.svg' alt='' />
						</div>
						<div className='flex flex-col items-center ml-auto'>
							<img
								src={call.avatars[1] || avatar}
								alt='receiver'
								className='w-24 h-24 rounded-full object-cover border-4 border-white shadow'
							/>
							<span className='mt-2 text-gray-700 font-medium'>{call.to}</span>
						</div>
					</div>
				</div>
				{/* Details */}
				<div className='flex flex-col gap-4 px-8 mb-8'>
					<div className='flex justify-between items-center'>
						<span className='text-gray-700 font-medium'>Date</span>
						<span className='text-gray-500'>
							{formatDateDmy(call?.originalData?.createdAt)}
						</span>
					</div>
					<div className='flex justify-between items-center'>
						<span className='text-gray-700 font-medium'>Start time</span>
						<span className='text-gray-500'>{startTime}</span>
					</div>
					<div className='flex justify-between items-center'>
						<span className='text-gray-700 font-medium'>End time</span>
						<span className='text-gray-500'>{endTimeFormatted}</span>
					</div>
					<div className='flex justify-between items-center'>
						<span className='text-gray-700 font-medium'>Call Duration</span>
						<span className='text-gray-500'>
							{call?.originalData?.call_duration
								? formatDuration(call?.originalData?.call_duration)
								: "N/A"}
						</span>
					</div>
					<div className='flex justify-between items-center'>
						<span className='text-gray-700 font-medium'>Status</span>
						<span
							className={`text-sm font-medium ${
								call?.call_status === "missed"
									? "text-red-500"
									: "text-green-500"
							}`}
						>
							{call?.call_status === "missed" ? "Missed" : "Completed"}
						</span>
					</div>
				</div>
				{/* Call recording - only show if call was completed and recording exists */}
				{call?.call_status !== "missed" && callRecordingUrl && (
					<div className='px-6'>
						<div className='bg-purple-100 rounded-xl p-4'>
							<div className='flex items-center justify-between mb-4'>
								<span className='text-gray-700 font-medium'>
									Call recording
								</span>
								<div className='flex gap-2'>
									{/* Direct download button */}
									<button
										className='text-gray-500 hover:text-purple-700 disabled:opacity-50 flex items-center gap-1'
										onClick={handleDownload}
										disabled={isLoading}
										title='Download recording'
									>
										{isLoading ? (
											<>
												<svg
													className='animate-spin h-4 w-4'
													viewBox='0 0 24 24'
												>
													<circle
														className='opacity-25'
														cx='12'
														cy='12'
														r='10'
														stroke='currentColor'
														strokeWidth='4'
														fill='none'
													/>
													<path
														className='opacity-75'
														fill='currentColor'
														d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
													/>
												</svg>
												<span className='text-xs'>Downloading...</span>
											</>
										) : (
											<>
												<Download />
												<span className='text-xs'>Download</span>
											</>
										)}
									</button>
								</div>
							</div>

							{/* Audio Player */}
							<div className='bg-white rounded-lg p-4'>
								<div className='flex items-center mb-3'>
									<img
										src={call.avatars[0] || avatar}
										alt='caller'
										className='w-8 h-8 rounded-full object-cover mr-3'
									/>
									<button
										onClick={togglePlay}
										className='w-10 h-10 flex items-center justify-center rounded-full bg-purple-600 text-white hover:bg-purple-700 transition-colors'
										disabled={!callRecordingUrl}
									>
										{isPlaying ? (
											<svg
												width='16'
												height='16'
												fill='currentColor'
												viewBox='0 0 24 24'
											>
												<rect x='6' y='4' width='4' height='16' />
												<rect x='14' y='4' width='4' height='16' />
											</svg>
										) : (
											<svg
												width='16'
												height='16'
												fill='currentColor'
												viewBox='0 0 24 24'
											>
												<polygon points='5,3 19,12 5,21' />
											</svg>
										)}
									</button>
									<div className='flex-1 ml-3'>
										<div className='flex justify-between text-xs text-gray-500 mb-1'>
											<span>{formatDuration(currentTime)}</span>
											<span>{formatDuration(duration)}</span>
										</div>
										<input
											type='range'
											min='0'
											max={duration || 0}
											value={currentTime}
											onChange={handleSeek}
											className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider'
											style={{
												background: `linear-gradient(to right, #9333ea 0%, #9333ea ${
													(currentTime / (duration || 1)) * 100
												}%, #e5e7eb ${
													(currentTime / (duration || 1)) * 100
												}%, #e5e7eb 100%)`,
											}}
										/>
									</div>
									<img
										src={call.avatars[1] || avatar}
										alt='receiver'
										className='w-8 h-8 rounded-full object-cover ml-3'
									/>
								</div>

								{/* Audio element (hidden) */}
								<audio
									ref={audioRef}
									src={callRecordingUrl}
									onTimeUpdate={handleTimeUpdate}
									onLoadedMetadata={handleLoadedMetadata}
									onEnded={() => setIsPlaying(false)}
									onError={() => {
										console.error("Audio playback error");
										setIsPlaying(false);
									}}
									controls
									style={{ display: "none" }}
									onContextMenu={(e) => {
										e.preventDefault();
										// Show custom context menu or trigger download
										if (callRecordingUrl) {
											window.open(callRecordingUrl, "_blank");
										}
									}}
								/>
							</div>
						</div>
					</div>
				)}
			</div>
		</>
	);
};

export default CallDetailsModal;
